import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { CodeBlock } from "@/components/code-block";
import { CopyButton } from "@/components/copy-button";
import { Button } from "@/components/ui/button";
import { useApiKey } from "@/hooks/use-auth";
import type { Challenge } from "@/lib/api";
import { RUN_COMMAND, dotenv, likelyShell, writeDotenv } from "@/lib/sdk";

/**
 * This challenge in two pastes: write the kit's `.env`, run an agent.
 *
 * The card assumes the tutorial's setup (uv, the cloned kit) and links to it for anyone
 * without. It carries only what differs here: the challenge id and the reader's key, in a
 * command that writes the file so there is no editor step, and the run line. Everything
 * that is true of every challenge -- what an agent is, what `run()` does, the baseline --
 * stays on the front page, and the API itself in the SDK reference.
 */
export function SdkSnippet({ challenge }: { challenge: Challenge }) {
  const apiKey = useApiKey();
  const [keyShown, setKeyShown] = useState(false);
  const shell = likelyShell();
  const file = dotenv(challenge.id, apiKey);
  const shown = dotenv(challenge.id, apiKey, { mask: !keyShown });

  return (
    <div className="min-w-0 space-y-3">
      <p className="text-muted-foreground text-sm">
        First time here? The tutorial installs uv and the kit, then walks through driving, your
        agent, and the baseline.{" "}
        <Link
          to="/"
          className="text-primary inline-flex items-center gap-0.5 font-medium underline-offset-4 hover:underline"
        >
          Start step by step
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </p>

      <Line
        step={1}
        title="Write .env"
        code={writeDotenv(shown, shell)}
        copy={writeDotenv(file, shell)}
        hint={
          apiKey
            ? "Paste in the kit folder. The key is masked here and real when copied. Edit this file to switch challenges."
            : "Paste in the kit folder. Sign in at the top right and your key is filled in."
        }
        action={
          apiKey ? (
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              onClick={() => setKeyShown((v) => !v)}
              aria-label={keyShown ? "Hide key" : "Show key"}
              aria-pressed={keyShown}
            >
              {keyShown ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
            </Button>
          ) : null
        }
      />
      <Line
        step={2}
        title="Run"
        code={RUN_COMMAND}
        hint="my_agent.py is yours to fill in. keyboard_agent.py drives by hand; baseline_agent.py is the baseline."
      />

      <p className="text-muted-foreground text-xs leading-relaxed">
        The exploration data downloads on first run into{" "}
        <code className="font-mono">data/{challenge.id}/</code>. A window opens and this page
        follows the run live. Return <code className="font-mono">Action.CHECKIN</code> where
        you think the goal is: that scores the run, and there is nothing to upload.
        {challenge.collects_report ? (
          <>
            {" "}
            Then{" "}
            <a href="#report" className="text-primary underline-offset-4 hover:underline">
              hand in your team&apos;s report
            </a>{" "}
            on this page.
          </>
        ) : challenge.final_submission_link ? (
          <>
            {" "}
            Then{" "}
            <a
              href={challenge.final_submission_link}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline-offset-4 hover:underline"
            >
              submit your report
            </a>
            .
          </>
        ) : null}
      </p>

      <p className="text-muted-foreground text-xs">
        The API itself:{" "}
        <Link
          to="/sdk"
          className="text-primary inline-flex items-center gap-0.5 underline-offset-4 hover:underline"
        >
          SDK reference
          <ArrowRight className="size-3" aria-hidden />
        </Link>
      </p>
    </div>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <span className="bg-primary/10 text-primary grid size-6 shrink-0 place-items-center rounded-md text-xs font-semibold tabular-nums">
      {n}
    </span>
  );
}

function Line({
  step,
  title,
  code,
  copy,
  hint,
  action,
}: {
  step: number;
  title: string;
  /** What is shown. */
  code: string;
  /** What is copied, when it differs from what is shown (a masked key). */
  copy?: string;
  hint: string;
  action?: React.ReactNode;
}) {
  return (
    <CodeBlock
      code={code}
      copy={copy}
      language="bash"
      className="bg-muted/40 rounded-lg"
      header={
        <>
          <StepNumber n={step} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{title}</p>
            <p className="text-muted-foreground text-xs">{hint}</p>
          </div>
          {action}
          <CopyButton text={copy ?? code} className="bg-background" />
        </>
      }
    />
  );
}
